import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';

const INVITE_ROLES = ['operator', 'admin'];
const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', operator: 'Operator' };
const ROLE_COLORS = {
  owner: 'bg-orange-500/20 text-orange-400',
  admin: 'bg-blue-500/20 text-blue-400',
  operator: 'bg-gray-600/30 text-gray-400',
};

// Separate client that never touches the admin's persisted session
const inviteClient = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  { auth: { persistSession: false, autoRefreshToken: false } }
);

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const { membership, isOwner } = useOrg();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchMembers = async () => {
    const { data } = await supabase
      .from('organisation_members')
      .select('id, user_id, role, created_at, profiles(email, full_name)')
      .eq('org_id', membership.org_id)
      .order('created_at');
    if (data) setMembers(data);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (membership?.org_id) fetchMembers();
  }, [membership?.org_id]);

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;

    setInviteLoading(true);
    setError('');
    setSuccess('');

    // 1. Create the auth user via the non-persistent client so the admin stays logged in.
    //    If the email already exists in Supabase auth, signUp returns the existing user
    //    without error (when email confirmation is disabled) — we handle that via the
    //    duplicate membership insert error below.
    const { data: signUpData, error: signUpError } = await inviteClient.auth.signUp({
      email,
      password: crypto.randomUUID(), // Random — user will never use this, reset email replaces it
      options: {
        data: { invited_to_org: membership.org_id },
      },
    });

    if (signUpError) {
      setError(`Could not create account: ${signUpError.message}`);
      setInviteLoading(false);
      return;
    }

    const newUserId = signUpData.user?.id;
    if (!newUserId) {
      setError('Unexpected error — no user ID returned. Please try again.');
      setInviteLoading(false);
      return;
    }

    // 2. Add to organisation
    const { error: memberError } = await supabase
      .from('organisation_members')
      .insert({ org_id: membership.org_id, user_id: newUserId, role: inviteRole });

    if (memberError) {
      if (memberError.code === '23505') {
        setError('This user is already a member of your organisation.');
      } else {
        setError(`Failed to add member: ${memberError.message}`);
      }
      setInviteLoading(false);
      return;
    }

    // 3. Send password reset email — this becomes their "set your password" first-login email
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setSuccess(`Invited ${email} as ${ROLE_LABELS[inviteRole]}. They'll receive an email to set their password.`);
    setInviteEmail('');
    fetchMembers();
    setInviteLoading(false);
  };

  const updateRole = async (memberId, newRole) => {
    await supabase
      .from('organisation_members')
      .update({ role: newRole })
      .eq('id', memberId);
    fetchMembers();
  };

  const removeMember = async (member) => {
    if (member.user_id === currentUser.id) {
      setError('You cannot remove yourself.');
      return;
    }
    const label = member.profiles?.full_name || member.profiles?.email || 'this user';
    if (!window.confirm(`Remove ${label} from the organisation?`)) return;
    await supabase.from('organisation_members').delete().eq('id', member.id);
    fetchMembers();
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header title="Users" backTo="/admin" />

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Invite form */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <div>
            <h3 className="text-white font-semibold text-sm">Invite Member</h3>
            <p className="text-gray-500 text-xs mt-0.5">
              They'll receive an email to set their password and will be added to your org automatically.
            </p>
          </div>

          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20">
              {error}
            </p>
          )}
          {success && (
            <p className="text-green-400 text-sm bg-green-500/10 rounded-lg px-3 py-2 border border-green-500/20">
              {success}
            </p>
          )}

          <form onSubmit={handleInvite} className="space-y-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); setError(''); setSuccess(''); }}
              placeholder="operator@company.com"
              className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />

            <div className="flex gap-2">
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="flex-1 bg-gray-700 text-white rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {INVITE_ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={inviteLoading}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold px-5 py-3 rounded-xl text-sm transition-colors"
              >
                {inviteLoading ? '…' : 'Invite'}
              </button>
            </div>
          </form>
        </div>

        {/* Members list */}
        <div>
          <h3 className="text-white font-semibold text-sm mb-3">
            Members ({members.length})
          </h3>

          {loading ? (
            <p className="text-gray-500 text-center py-6">Loading…</p>
          ) : (
            <div className="space-y-2.5">
              {members.map((member) => {
                const isSelf = member.user_id === currentUser.id;
                const isTargetOwner = member.role === 'owner';

                return (
                  <div key={member.id} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {member.profiles?.full_name || member.profiles?.email || '(pending)'}
                          {isSelf && <span className="text-gray-500 text-xs ml-1">(you)</span>}
                        </p>
                        {member.profiles?.full_name && (
                          <p className="text-gray-500 text-xs truncate">{member.profiles?.email}</p>
                        )}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${ROLE_COLORS[member.role]}`}>
                        {ROLE_LABELS[member.role]}
                      </span>
                    </div>

                    {!isSelf && !isTargetOwner && (
                      <div className="flex gap-2 mt-3">
                        <select
                          value={member.role}
                          onChange={(e) => updateRole(member.id, e.target.value)}
                          className="flex-1 bg-gray-700 text-white text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
                        >
                          {(isOwner ? ['operator', 'admin', 'owner'] : ['operator', 'admin']).map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => removeMember(member)}
                          className="text-xs text-red-400 hover:text-red-300 px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
